import { formatDisplayDate } from './dateDisplay';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Merge Tailwind classes with clsx (used by shadcn/ui components) */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format a number as AUD currency */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(amount);
}

/** Format an ISO date string for AU locale */
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  return formatDisplayDate(date, options);
}

/** Format an ISO date string as a time (e.g. "2:30 PM") */
export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? parseDateValue(date) : date;
  return d.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Format date + time together */
export function formatDateTime(date: string | Date): string {
  return `${formatDate(date)} ${formatTime(date)}`;
}

/** Format a Date as a local calendar date for database date-only comparisons */
export function toDateOnlyString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Calculate hours between two ISO date strings */
export function calculateHours(start: string, end: string, breakMinutes = 0): number {
  const startDate = parseDateValue(start);
  const endDate = parseDateValue(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 0;
  }

  let ms = endDate.getTime() - startDate.getTime();
  if (ms < 0) {
    ms += 24 * 60 * 60 * 1000;
  }
  const hours = ms / (1000 * 60 * 60) - breakMinutes / 60;
  return Math.max(0, Math.round(hours * 100) / 100);
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_ONLY_PATTERN = /^\d{1,2}:\d{2}(?::\d{2})?$/;

function parseDateValue(value: string): Date {
  if (DATE_ONLY_PATTERN.test(value)) {
    return new Date(`${value}T00:00:00`);
  }

  if (TIME_ONLY_PATTERN.test(value)) {
    const [hours, minutes, seconds = '0'] = value.split(':');
    return new Date(1970, 0, 1, Number(hours), Number(minutes), Number(seconds));
  }

  return new Date(value);
}

/** Convert hex colour to RGB components */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length !== 6) return null;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return null;
  return { r, g, b };
}

/** Get user initials from a full name */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
